import {
  SHStack,
  SHTable,
  SHTableBody,
  SHTableContainer,
  SHTableHead,
  SHTableRow,
  SHTypography,
} from "@components/design-systems";
import { AnalysisCommentDTO } from "@models/platform-analysis/entities/comment";
import {
  ProductRecommendationDTO,
  RenderAnalysisSummaryItemProps,
} from "@models/platform-analysis/entities/steps/summary";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import {
  CommentFeatureMatchColWidthPercent,
  CommentProductColWidthPercent,
  CommentSettingFieldPath,
} from "@pages/platform-analysis-v2/_id/constants";
import { RootState } from "@redux/store";
import { isNil } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { SummaryCommentSettingCheckbox } from "../comments/check-box";
import { SummaryCommentHeaderCell } from "../comments/header-cell";
import { SummaryCommentRow } from "../comments/row";
import { getCommentColWidth } from "../comments/utils";

export const SummaryPlatformSpecificComments = memo(
  ({ disabled: sectionDisabled }: RenderAnalysisSummaryItemProps) => {
    //#region Hooks & States
    const { analysisId } = useParams<{ analysisId: string }>();
    const analysisSteps = useSelector(
      (state: RootState) => state.platformAnalysis.analysisSteps,
    );
    const analysisComments = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary?.analysisComments,
    );
    const commentSettings = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary
          ?.specificCommentSetting,
    );
    const isCompleted = useSelector(
      (state: RootState) => state.platformAnalysis.isCompleted,
    );
    const isReadOnly = useSelector(
      (state: RootState) => state.platformAnalysis.isReadOnly,
    );
    const productRecommendations = useSelector(
      (state: RootState) =>
        state.platformAnalysis.internal.summary.productRecommendations,
    );
    const firmName = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary?.firmName,
    );

    const [productCommentOrdered, setProductCommentOrdered] = useState<
      AnalysisCommentDTO[]
    >([]);

    const commentIndexMap = useMemo(() => {
      if (!analysisComments) return {};

      return analysisComments.reduce<Record<string, number>>(
        (acc, item, index) => {
          acc[item.productId] = index;

          return acc;
        },
        {},
      );
    }, [analysisComments]);

    const analysisCommentSteps = useMemo(() => {
      const displaySteps = analysisSteps?.steps?.filter(
        (item) => item !== AnalysisStep.Setup,
      );

      if (!isCompleted) return displaySteps;

      return displaySteps?.filter((step) => {
        switch (step) {
          case AnalysisStep.Feature:
            return commentSettings?.showFeature;
          case AnalysisStep.BusinessMetric:
            return commentSettings?.showBusinessMetric;
          case AnalysisStep.Fee:
            return commentSettings?.showFee;
          case AnalysisStep.Summary:
            return commentSettings?.showSummary;
          default:
            return true;
        }
      });
    }, [isCompleted, analysisSteps, commentSettings]);

    const columnsWidth = useMemo(
      () => getCommentColWidth(analysisCommentSteps?.length ?? 0),
      [analysisCommentSteps],
    );

    //#region Methods

    const sortCommentsByProductOrder = (
      comments: AnalysisCommentDTO[],
      recommendations: Record<string, Partial<ProductRecommendationDTO>>,
    ) =>
      [...comments].sort(
        (a, b) =>
          (recommendations[a.productId]?.order ?? Number.MAX_SAFE_INTEGER) -
          (recommendations[b.productId]?.order ?? Number.MAX_SAFE_INTEGER),
      );

    //#region Effect
    useEffect(() => {
      if (!analysisComments) return;

      setProductCommentOrdered(
        sortCommentsByProductOrder(
          analysisComments.filter((item) => item.inFinalShortlist),
          productRecommendations,
        ),
      );
    }, [analysisComments, productRecommendations]);

    //#region Render
    if (isNil(analysisCommentSteps)) return <></>;

    return (
      <SHStack width={"100%"} gap={2}>
        <SHTypography disabled={sectionDisabled} variant="subtitle2">
          Platforms selected for {firmName}
        </SHTypography>

        <SHTableContainer>
          <SHTable>
            <SHTableHead>
              <SHTableRow>
                <SummaryCommentHeaderCell
                  width={`${CommentProductColWidthPercent}%`}
                >
                  Platform
                </SummaryCommentHeaderCell>
                <SummaryCommentHeaderCell
                  width={`${CommentFeatureMatchColWidthPercent}%`}
                >
                  Features match
                </SummaryCommentHeaderCell>
                {analysisCommentSteps?.map((step) => {
                  const fieldPath = CommentSettingFieldPath[step];
                  if (fieldPath === undefined) return <></>;

                  return (
                    <SummaryCommentHeaderCell
                      key={step}
                      width={`${columnsWidth}%`}
                    >
                      <SHStack
                        flexDirection={"row"}
                        justifyContent={"center"}
                        gap={1}
                        alignItems={"center"}
                      >
                        {!isCompleted && (
                          <SummaryCommentSettingCheckbox
                            fieldPath={`specificCommentSetting.${fieldPath}`}
                            isDisabled={isReadOnly || sectionDisabled}
                          />
                        )}

                        <SHTypography
                          disabled={sectionDisabled}
                          variant="subtitle1"
                          fontWeight={700}
                          textAlign="center"
                          lineHeight={"120%"}
                        >
                          {step}
                        </SHTypography>
                      </SHStack>
                    </SummaryCommentHeaderCell>
                  );
                })}
              </SHTableRow>
            </SHTableHead>

            <SHTableBody>
              {productCommentOrdered?.map((item) => {
                return (
                  <SummaryCommentRow
                    key={`analysisComments.${commentIndexMap[item.productId]}`}
                    analysisId={analysisId ?? ""}
                    rowPath={`analysisComments.${
                      commentIndexMap[item.productId]
                    }`}
                    commentSettingFieldPath={"specificCommentSetting"}
                    columnWidth={columnsWidth}
                    rowData={item}
                    steps={analysisCommentSteps}
                    isAppendix={false}
                    isDisabled={isReadOnly || sectionDisabled}
                    isViewMode={!!isCompleted}
                    recommendationData={productRecommendations[item.productId]}
                  />
                );
              })}
            </SHTableBody>
          </SHTable>
        </SHTableContainer>
      </SHStack>
    );
  },
);
